/**
 * Переключатель агрегации графика: Daily / Weekly / Monthly.
 *
 * ★ Когда доступен один режим, список не рисуется вовсе — вместо него
 * подпись «Daily». Выпадающий список с единственным пунктом обещает
 * выбор, которого нет, и человек открывает его, чтобы это выяснить.
 */
import { useEffect, useRef, useState } from "react";
import { useLayer } from "@/lib/layer-stack";

import type { ChartPeriod } from "@/lib/entity-chart-metrics";

const LABEL: Record<ChartPeriod, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

export function PeriodPicker({
  value,
  options,
  onChange,
}: {
  value: ChartPeriod;
  options: ChartPeriod[];
  onChange: (p: ChartPeriod) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  useLayer(open, () => setOpen(false));

  if (options.length <= 1) {
    return <span className="chart-period-static">{LABEL[value]}</span>;
  }

  return (
    <div className="chart-period" ref={wrap}>
      <button
        type="button"
        className="btn"
        style={{ fontSize: 12 }}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{LABEL[value]}</span>
        <svg
          width="11" height="11" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          style={{ color: "var(--c-text-3)" }}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="chart-period-menu" role="listbox">
          {options.map((p) => (
            <button
              type="button"
              key={p}
              role="option"
              aria-selected={p === value}
              className={p === value ? "is-selected" : ""}
              onClick={() => {
                onChange(p);
                setOpen(false);
              }}
            >
              {LABEL[p]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
